import {Pattern, match} from 'ts-pattern';

import type {AppSyncWebSocketUrlResult} from '../types';
import {buildAuthHeader} from './buildAuthHeader';
import {resolveAppSyncHost} from './resolveAppSyncHost';

const EMPTY_PAYLOAD = 'e30=';
const MISSING_URL_REASON = 'AppSync URL is not configured';
const MALFORMED_URL_REASON = 'AppSync URL is malformed';

const toRealtimeUrl = (appSyncUrl: string, headerBase64: string) => {
  const wsUrl = appSyncUrl
    .replace('https://', 'wss://')
    .replace(/^http:\/\//, 'ws://')
    .replace('appsync-api', 'appsync-realtime-api');

  return `${wsUrl}/realtime?header=${headerBase64}&payload=${EMPTY_PAYLOAD}`;
};

/**
 * Builds the realtime WebSocket URL and classifies every failure, so the
 * caller can tell a recoverable gap (no access token yet) from a deterministic
 * configuration fault that no reconnect can fix.
 */
export const buildWebSocketUrl = (): AppSyncWebSocketUrlResult =>
  match({
    hostResolution: resolveAppSyncHost(process.env.APPSYNC_URL),
    authHeader: buildAuthHeader(),
  })
    .returnType<AppSyncWebSocketUrlResult>()
    .with({hostResolution: {status: 'notConfigured'}}, () => ({
      status: 'misconfigured',
      reason: MISSING_URL_REASON,
    }))
    .with({hostResolution: {status: 'malformed'}}, () => ({
      status: 'misconfigured',
      reason: MALFORMED_URL_REASON,
    }))
    .with(
      {hostResolution: {status: 'resolved'}, authHeader: Pattern.nonNullable},
      ({hostResolution, authHeader}) => ({
        status: 'ready',
        url: toRealtimeUrl(hostResolution.appSyncUrl, btoa(JSON.stringify(authHeader))),
      })
    )
    .otherwise(() => ({status: 'unauthenticated'}));
